import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import axios from '../../../../redux/apiCaller';
import { updateTopic } from '../../redux/actions/topic';
import {
  Grid,
  makeStyles,
  Paper,
  TextField,
  Slider,
  Typography,
  Tooltip,
  IconButton,
  Divider,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
} from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import RefreshIcon from '@material-ui/icons/Refresh';
import FormatSizeIcon from '@material-ui/icons/FormatSize';
import UndoIcon from '@material-ui/icons/Undo';
import RedoIcon from '@material-ui/icons/Redo';
import SaveIcon from '@material-ui/icons/Save';

/* ─── Load the custom font ─────────────────────────────────────────── */
// Font contains: A–Z, a–z, Ñ, á é í ó ú ñ, space only.
// No digits, no punctuation, no uppercase accented vowels (Á É Í Ó Ú).
// Discretionary ligatures: CH, LL, RR (enable via font-variant-ligatures).
const FONT_URL = '/font/Vivamos%20Puff%20Alt.ttf';
const FONT_FAMILY = 'Vivamos Puff Alt';

const fontFaceStyle = `
  @font-face {
    font-family: '${FONT_FAMILY}';
    src: url('${FONT_URL}') format('truetype');
    font-weight: normal;
    font-style: normal;
  }
`;

/* ─── Only the characters the font actually contains ──────────────── */
const CHAR_SETS = [
  { label: 'A–Z (Uppercase)', chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' },
  { label: 'a–z (Lowercase)', chars: 'abcdefghijklmnopqrstuvwxyz' },
  { label: 'Spanish accented', chars: 'áéíóúñÑ' },
  { label: 'Ligatures (CH · LL · RR)', chars: ['CH', 'LL', 'RR'] },
];

const DEFAULT_TEXT = 'Hola Mundo';
const DEFAULT_SIZE = 96;
const DEFAULT_COLOR = '#1a1a2e';
const DEFAULT_BG = 'transparent';
const LANG_ENGLISH = 'english';
const LANG_SPANISH = 'spanish';

const emptySnapshot = (text) => ({
  presetId: null,
  text: text || DEFAULT_TEXT,
  fontSize: DEFAULT_SIZE,
  textColor: DEFAULT_COLOR,
  bgColor: DEFAULT_BG,
  letterSpacing: 2,
  wordSpacing: 0,
  tokenSizes: {},
  tokenWidths: {},
  tokenHeights: {},
  tokenSpacings: {},
  undoStack: [],
  redoStack: [],
});

const snapshotFromPreset = (preset, fallbackText, presetId) => ({
  ...emptySnapshot(fallbackText),
  presetId: preset?._id || presetId || null,
  text: preset?.text || fallbackText || DEFAULT_TEXT,
  fontSize: Number(preset?.fontSize) || DEFAULT_SIZE,
  textColor: preset?.textColor || DEFAULT_COLOR,
  bgColor: preset?.bgColor || DEFAULT_BG,
  letterSpacing: preset?.letterSpacing ?? 2,
  wordSpacing: preset?.wordSpacing ?? 0,
  tokenSizes: preset?.tokenSizes || {},
  tokenWidths: preset?.tokenWidths || {},
  tokenHeights: preset?.tokenHeights || {},
  tokenSpacings: preset?.tokenSpacings || {},
});

const fallbackTextForLang = (topic, lang) => {
  if (lang === LANG_SPANISH) return topic?.spanishName || topic?.name || '';
  return topic?.name || '';
};

const loadPresetSnapshot = async (id, fallbackText) => {
  if (!id) return emptySnapshot(fallbackText);
  try {
    const res = await axios.get(`/admin/font-presets/${id}`);
    return snapshotFromPreset(res.data?.preset, fallbackText, id);
  } catch (err) {
    console.error('Failed to load title preset', err);
    return { ...emptySnapshot(fallbackText), presetId: id };
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '0 16px 32px',
  },
  paper: {
    padding: '24px',
    borderRadius: 12,
    marginBottom: 20,
  },
  /* ── preview panel ── */
  previewWrapper: {
    position: 'relative',
    width: '100%',
  },
  previewBg: {
    minHeight: 260,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 32px',
    transition: 'background 0.3s ease',
  },
  previewText: {
    fontFamily: `'${FONT_FAMILY}', cursive`,
    lineHeight: 1.2,
    wordBreak: 'break-word',
    textAlign: 'center',
    transition: 'color 0.2s ease, font-size 0.15s ease',
    userSelect: 'none',
    letterSpacing: 2,
    fontKerning: 'normal',
    fontVariantLigatures: 'discretionary-ligatures',
  },
  previewPlaceholder: {
    fontFamily: `'${FONT_FAMILY}', cursive`,
    opacity: 0.35,
    fontSize: 72,
    color: '#555',
    userSelect: 'none',
    letterSpacing: 2,
  },
  /* ── controls ── */
  sectionTitle: {
    fontWeight: 600,
    marginBottom: 10,
    color: '#444',
    fontSize: 13,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  inputField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      fontFamily: 'inherit',
      fontSize: 18,
    },
  },
  sliderLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  slider: {
    color: '#533483',
  },
  colorGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 6,
  },
  colorSwatch: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    cursor: 'pointer',
    border: '2px solid transparent',
    transition: 'transform 0.15s, border-color 0.15s',
    '&:hover': {
      transform: 'scale(1.2)',
    },
  },
  colorSwatchSelected: {
    border: '2px solid #533483',
    transform: 'scale(1.2)',
  },
  colorInput: {
    width: 42,
    height: 42,
    border: 'none',
    padding: 0,
    borderRadius: 8,
    cursor: 'pointer',
    background: 'none',
  },
  charChip: {
    margin: 2,
    fontFamily: `'${FONT_FAMILY}', cursive`,
    fontSize: 16,
    cursor: 'pointer',
    '&:hover': {
      background: '#533483',
      color: '#fff',
    },
  },
  charSetRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 6,
  },
  charSetLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#888',
    marginTop: 10,
    marginBottom: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    padding: '8px 12px',
    background: 'rgba(0,0,0,0.04)',
    borderTop: '1px solid rgba(0,0,0,0.06)',
  },
  fontBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 12px',
    background: 'rgba(83,52,131,0.1)',
    borderRadius: 20,
    marginBottom: 16,
    width: 'fit-content',
  },
  fontBadgeText: {
    fontSize: 12,
    color: '#533483',
    fontWeight: 600,
  },
  langTabs: {
    minHeight: 40,
    margin: '0 24px 8px',
  },
  langSubtitle: {
    fontSize: 13,
    color: '#666',
    margin: '0 24px 8px',
  },
}));

export default function TopicTitleEditor({ open, topic, onClose }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [fontLoaded, setFontLoaded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [presetId, setPresetId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeLang, setActiveLang] = useState(LANG_ENGLISH);

  const [text, setText] = useState(DEFAULT_TEXT);
  const [fontSize, setFontSize] = useState(DEFAULT_SIZE);
  const [textColor, setTextColor] = useState(DEFAULT_COLOR);
  const [bgColor, setBgColor] = useState(DEFAULT_BG);

  const [letterSpacing, setLetterSpacing] = useState(2);
  const [wordSpacing, setWordSpacing] = useState(0);
  const [selectedTokenIndices, setSelectedTokenIndices] = useState([]);
  const [tokenSizes, setTokenSizes] = useState({});
  const [tokenWidths, setTokenWidths] = useState({});
  const [tokenHeights, setTokenHeights] = useState({});
  const [tokenSpacings, setTokenSpacings] = useState({});
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const previewRef = React.useRef(null);
  const dragStartTokenRef = React.useRef(null);
  const hasDraggedRef = React.useRef(false);
  const ignoreNextTokenClickRef = React.useRef(false);
  const ignoreNextPreviewClickRef = React.useRef(false);
  const activeLangRef = React.useRef(LANG_ENGLISH);
  const versionsRef = React.useRef({
    [LANG_ENGLISH]: emptySnapshot(),
    [LANG_SPANISH]: emptySnapshot(),
  });

  const applySnapshot = (snapshot) => {
    setPresetId(snapshot?.presetId || null);
    setText(snapshot?.text || DEFAULT_TEXT);
    setFontSize(Number(snapshot?.fontSize) || DEFAULT_SIZE);
    setTextColor(snapshot?.textColor || DEFAULT_COLOR);
    setBgColor(snapshot?.bgColor || DEFAULT_BG);
    setLetterSpacing(snapshot?.letterSpacing ?? 2);
    setWordSpacing(snapshot?.wordSpacing ?? 0);
    setTokenSizes(snapshot?.tokenSizes || {});
    setTokenWidths(snapshot?.tokenWidths || {});
    setTokenHeights(snapshot?.tokenHeights || {});
    setTokenSpacings(snapshot?.tokenSpacings || {});
    setSelectedTokenIndices([]);
    setUndoStack(snapshot?.undoStack || []);
    setRedoStack(snapshot?.redoStack || []);
  };

  const captureCurrentVersion = () => {
    versionsRef.current[activeLangRef.current] = {
      presetId,
      text,
      fontSize,
      textColor,
      bgColor,
      letterSpacing,
      wordSpacing,
      tokenSizes: { ...tokenSizes },
      tokenWidths: { ...tokenWidths },
      tokenHeights: { ...tokenHeights },
      tokenSpacings: { ...tokenSpacings },
      undoStack,
      redoStack,
    };
  };

  const handleLangChange = (_, lang) => {
    if (!lang || lang === activeLangRef.current) return;
    captureCurrentVersion();
    activeLangRef.current = lang;
    setActiveLang(lang);
    applySnapshot(versionsRef.current[lang]);
  };

  useEffect(() => {
    if (!open || !topic) return;
    let cancelled = false;
    const englishFallback = fallbackTextForLang(topic, LANG_ENGLISH);
    const spanishFallback = fallbackTextForLang(topic, LANG_SPANISH);

    Promise.all([
      loadPresetSnapshot(topic.fontPresetId, englishFallback),
      loadPresetSnapshot(topic.spanishFontPresetId, spanishFallback),
    ]).then(([english, spanish]) => {
      if (cancelled) return;
      versionsRef.current = { [LANG_ENGLISH]: english, [LANG_SPANISH]: spanish };
      activeLangRef.current = LANG_ENGLISH;
      setActiveLang(LANG_ENGLISH);
      applySnapshot(english);
    });

    return () => {
      cancelled = true;
    };
  }, [open, topic]);

  const getHistoryState = () => ({
    text,
    fontSize,
    textColor,
    bgColor,
    letterSpacing,
    wordSpacing,
    tokenSizes: { ...tokenSizes },
    tokenWidths: { ...tokenWidths },
    tokenHeights: { ...tokenHeights },
    tokenSpacings: { ...tokenSpacings },
  });

  const applyHistoryState = (state) => {
    setText(state.text);
    setFontSize(state.fontSize);
    setTextColor(state.textColor);
    setBgColor(state.bgColor);
    setLetterSpacing(state.letterSpacing);
    setWordSpacing(state.wordSpacing);
    setTokenSizes(state.tokenSizes);
    setTokenWidths(state.tokenWidths);
    setTokenHeights(state.tokenHeights);
    setTokenSpacings(state.tokenSpacings);
  };

  const saveStateToHistory = () => {
    setUndoStack((prev) => [...prev, getHistoryState()].slice(-50)); // Keep last 50 actions
    setRedoStack([]);
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const lastState = undoStack[undoStack.length - 1];
    setRedoStack((prev) => [...prev, getHistoryState()].slice(-50));
    applyHistoryState(lastState);
    setUndoStack((prev) => prev.slice(0, -1));
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const nextState = redoStack[redoStack.length - 1];
    setUndoStack((prev) => [...prev, getHistoryState()].slice(-50));
    applyHistoryState(nextState);
    setRedoStack((prev) => prev.slice(0, -1));
  };

  useEffect(() => {
    const stopDragging = () => {
      if (hasDraggedRef.current) {
        ignoreNextTokenClickRef.current = true;
        ignoreNextPreviewClickRef.current = true;
        setTimeout(() => {
          ignoreNextTokenClickRef.current = false;
          ignoreNextPreviewClickRef.current = false;
        }, 0);
      }
      dragStartTokenRef.current = null;
      hasDraggedRef.current = false;
    };

    window.addEventListener('mouseup', stopDragging);
    return () => window.removeEventListener('mouseup', stopDragging);
  }, []);

  /* inject @font-face once */
  useEffect(() => {
    let styleEl = document.getElementById('themed-letters-font');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'themed-letters-font';
      styleEl.textContent = fontFaceStyle;
      document.head.appendChild(styleEl);
    }
    /* probe font loading */
    if (document.fonts && document.fonts.load) {
      document.fonts.load(`48px '${FONT_FAMILY}'`).then(() => setFontLoaded(true));
    } else {
      setTimeout(() => setFontLoaded(true), 800);
    }
  }, []);

  const handleReset = () => {
    saveStateToHistory();
    setFontSize(DEFAULT_SIZE);
    setTextColor(DEFAULT_COLOR);
    setBgColor(DEFAULT_BG);
    setLetterSpacing(2);
    setWordSpacing(0);
    setSelectedTokenIndices([]);
    setTokenSizes({});
    setTokenWidths({});
    setTokenHeights({});
    setTokenSpacings({});
  };

  const saveVersionPreset = async (snapshot) => {
    const presetData = {
      text: snapshot.text,
      fontSize: snapshot.fontSize,
      textColor: snapshot.textColor,
      bgColor: snapshot.bgColor,
      letterSpacing: snapshot.letterSpacing,
      wordSpacing: snapshot.wordSpacing,
      tokenSizes: snapshot.tokenSizes,
      tokenWidths: snapshot.tokenWidths,
      tokenHeights: snapshot.tokenHeights,
      tokenSpacings: snapshot.tokenSpacings,
    };
    if (snapshot.presetId) presetData._id = snapshot.presetId;
    const res = await axios.post(`/admin/font-presets`, presetData);
    return res.data?.preset?._id || snapshot.presetId || null;
  };

  const handleSavePreset = () => {
    if (!topic?._id || saving) return;
    captureCurrentVersion();
    const english = versionsRef.current[LANG_ENGLISH];
    const spanish = versionsRef.current[LANG_SPANISH];

    setSaving(true);
    Promise.all([saveVersionPreset(english), saveVersionPreset(spanish)])
      .then(([englishId, spanishId]) => {
        versionsRef.current[LANG_ENGLISH] = { ...english, presetId: englishId };
        versionsRef.current[LANG_SPANISH] = { ...spanish, presetId: spanishId };
        if (activeLangRef.current === LANG_ENGLISH) setPresetId(englishId);
        if (activeLangRef.current === LANG_SPANISH) setPresetId(spanishId);
        dispatch(
          updateTopic(topic._id, {
            fontPresetId: englishId,
            spanishFontPresetId: spanishId,
          }),
        );
      })
      .catch((err) => console.error('Failed to save preset', err))
      .finally(() => setSaving(false));
  };

  const tokens = React.useMemo(() => {
    const arr = [];
    let i = 0;
    let tIdx = 0;
    while (i < text.length) {
      const two = text.substr(i, 2);
      if (two === 'CH' || two === 'LL' || two === 'RR') {
        arr.push({ char: two, id: tIdx });
        i += 2;
      } else {
        arr.push({ char: text[i], id: tIdx });
        i += 1;
      }
      tIdx++;
    }
    return arr;
  }, [text]);

  useEffect(() => {
    const validIds = tokens.map((t) => t.id);
    setSelectedTokenIndices((prev) => prev.filter((id) => validIds.includes(id)));
  }, [tokens]);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const appendChar = (char) => {
    saveStateToHistory();
    setText((prev) => prev + char);
  };

  const selectToken = (tokenId) => {
    setSelectedTokenIndices((prev) => (prev.includes(tokenId) ? prev : [...prev, tokenId]));
  };

  const handleTokenMouseDown = (tokenId) => {
    dragStartTokenRef.current = tokenId;
    hasDraggedRef.current = false;
  };

  const handleTokenMouseEnter = (tokenId) => {
    if (dragStartTokenRef.current === null) return;
    hasDraggedRef.current = true;
    selectToken(dragStartTokenRef.current);
    selectToken(tokenId);
  };

  const handleTokenClick = (tokenId) => {
    if (ignoreNextTokenClickRef.current) {
      ignoreNextTokenClickRef.current = false;
      return;
    }
    setSelectedTokenIndices((prev) =>
      prev.includes(tokenId) ? prev.filter((id) => id !== tokenId) : [...prev, tokenId],
    );
  };

  const handlePreviewClick = (event) => {
    if (ignoreNextPreviewClickRef.current) {
      ignoreNextPreviewClickRef.current = false;
      return;
    }
    const target = event.target;
    if (!target.closest || !target.closest('[data-letter-token="true"]')) {
      setSelectedTokenIndices([]);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>{topic?.name ? `Title style — ${topic.name}` : 'Title style'}</DialogTitle>
      <Tabs
        className={classes.langTabs}
        value={activeLang}
        onChange={handleLangChange}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab value={LANG_ENGLISH} label="English" />
        <Tab value={LANG_SPANISH} label="Spanish" />
      </Tabs>
      <Typography className={classes.langSubtitle}>
        Editing {activeLang === LANG_SPANISH ? 'Spanish' : 'English'}
        {activeLang === LANG_SPANISH
          ? topic?.spanishName
            ? ` — ${topic.spanishName}`
            : ''
          : topic?.name
            ? ` — ${topic.name}`
            : ''}
      </Typography>
      <DialogContent>
        <div className={classes.root}>
          <Grid container spacing={3}>
            {/* ──────────── LEFT: preview ──────────── */}
            <Grid item xs={12} md={8}>
              {/* Font badge */}
              <div className={classes.fontBadge}>
                <FormatSizeIcon style={{ fontSize: 16, color: '#533483' }} />
                <span className={classes.fontBadgeText}>font/Vivamos Puff Alt.ttf</span>
                {fontLoaded ? (
                  <span style={{ fontSize: 11, color: '#4caf50', marginLeft: 4 }}>● Loaded</span>
                ) : (
                  <span style={{ fontSize: 11, color: '#f5a623', marginLeft: 4 }}>● Loading…</span>
                )}
              </div>

              {/* Font capability note */}
              <div
                style={{
                  marginBottom: 16,
                  padding: '8px 14px',
                  background: '#fffde7',
                  border: '1px solid #ffe082',
                  borderRadius: 8,
                  fontSize: 12,
                  color: '#6d4c00',
                  lineHeight: 1.6,
                  maxWidth: 680,
                }}
              >
                <strong>ⓘ Font character support:</strong> A–Z · a–z · á é í ó ú ñ Ñ · space only.
                <br />
                Discretionary ligatures: <strong>CH · LL · RR</strong> (rendered as single artwork glyphs).
                <br />
                <em>Digits, punctuation and uppercase accented vowels are not part of this font.</em>
              </div>

              <div style={{ marginBottom: 20 }}>
                {/* Preview area */}
                <div className={classes.previewWrapper}>
                  <div
                    className={classes.previewBg}
                    style={{ background: bgColor }}
                    onClick={handlePreviewClick}
                    data-testid="font-preview-background"
                  >
                    {text ? (
                      <div
                        ref={previewRef}
                        className={classes.previewText}
                        style={{
                          color: textColor,
                          letterSpacing: `${letterSpacing}px`,
                          wordSpacing: `${wordSpacing}px`,
                        }}
                      >
                        {tokens.map((tok) => (
                          <span
                            key={tok.id}
                            data-letter-token="true"
                            onMouseDown={() => handleTokenMouseDown(tok.id)}
                            onMouseEnter={() => handleTokenMouseEnter(tok.id)}
                            onClick={() => handleTokenClick(tok.id)}
                            style={{
                              fontSize: tokenSizes[tok.id] || fontSize,
                              display: 'inline-block',
                              transform: `scaleX(${(tokenWidths[tok.id] ?? 100) / 100}) scaleY(${
                                (tokenHeights[tok.id] ?? 100) / 100
                              })`,
                              transformOrigin: 'bottom center',
                              marginRight: (tokenSpacings[tok.id] || 0) + (tok.char === ' ' ? wordSpacing : 0),
                              cursor: 'pointer',
                              outline: selectedTokenIndices.includes(tok.id)
                                ? '2px dashed rgba(83,52,131,0.5)'
                                : 'none',
                              outlineOffset: '2px',
                              whiteSpace: 'pre-wrap',
                            }}
                            title="Click to adjust properties"
                          >
                            {tok.char}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className={classes.previewPlaceholder}>Type something…</span>
                    )}
                  </div>

                  {/* Action bar below preview */}
                  <div className={classes.actionBar} style={{ background: 'transparent', borderTop: 'none' }}>
                    <Tooltip title="Save title style">
                      <span>
                        <IconButton size="small" onClick={handleSavePreset} disabled={saving || !topic}>
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Undo">
                      <span>
                        <IconButton
                          aria-label="Undo"
                          size="small"
                          onClick={handleUndo}
                          disabled={undoStack.length === 0}
                        >
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Redo">
                      <span>
                        <IconButton
                          aria-label="Redo"
                          size="small"
                          onClick={handleRedo}
                          disabled={redoStack.length === 0}
                        >
                          <RedoIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={copied ? 'Copied!' : 'Copy text'}>
                      <IconButton size="small" onClick={handleCopy}>
                        <FileCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset to defaults">
                      <IconButton size="small" onClick={handleReset}>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>

              {/* Text input */}
              <Paper elevation={3} className={classes.paper}>
                <div style={{ padding: '20px 24px 24px' }}>
                  <Typography className={classes.sectionTitle}>Your Text</Typography>
                  <TextField
                    id="font-preview-input"
                    className={classes.inputField}
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    value={text}
                    onChange={(e) => {
                      saveStateToHistory();
                      setText(e.target.value);
                    }}
                    placeholder="Type letters here to preview…"
                  />
                </div>
              </Paper>

              {/* ── Character palette ── */}
              <Paper elevation={3} className={classes.paper}>
                <Typography className={classes.sectionTitle}>Character Palette — click to append</Typography>
                {CHAR_SETS.map((set) => (
                  <React.Fragment key={set.label}>
                    <div className={classes.charSetLabel}>{set.label}</div>
                    <div className={classes.charSetRow}>
                      {/* chars can be a string (split into single chars) or an array of multi-char strings */}
                      {(Array.isArray(set.chars) ? set.chars : set.chars.split('')).map((ch) => (
                        <Chip
                          key={ch}
                          label={ch}
                          size="small"
                          className={classes.charChip}
                          onClick={() => appendChar(ch)}
                          style={{
                            fontFamily: `'${FONT_FAMILY}', cursive`,
                            fontVariantLigatures: 'discretionary-ligatures',
                            fontSize: 15,
                          }}
                        />
                      ))}
                    </div>
                  </React.Fragment>
                ))}
              </Paper>
            </Grid>

            {/* ──────────── RIGHT: controls ──────────── */}
            <Grid item xs={12} md={4}>
              <Paper elevation={3} className={classes.paper}>
                {/* Font size */}
                <Typography className={classes.sectionTitle}>Global Font Size</Typography>
                <div className={classes.sliderLabel}>
                  <Typography variant="body2" style={{ color: '#777' }}>
                    {fontSize} px
                  </Typography>
                  <Typography variant="caption" style={{ color: '#aaa' }}>
                    12 – 120
                  </Typography>
                </div>
                <Slider
                  className={classes.slider}
                  value={fontSize}
                  min={12}
                  max={120}
                  step={2}
                  onMouseDown={saveStateToHistory}
                  onChange={(_, val) => setFontSize(val)}
                />

                <Divider style={{ margin: '20px 0' }} />

                {/* Letter Spacing */}
                <Typography className={classes.sectionTitle}>Letter Spacing</Typography>
                <div className={classes.sliderLabel}>
                  <Typography variant="body2" style={{ color: '#777' }}>
                    {letterSpacing} px
                  </Typography>
                </div>
                <Slider
                  className={classes.slider}
                  value={letterSpacing}
                  min={-20}
                  max={100}
                  step={1}
                  onMouseDown={saveStateToHistory}
                  onChange={(_, val) => setLetterSpacing(val)}
                />

                <Divider style={{ margin: '20px 0' }} />

                {/* Word Spacing */}
                <Typography className={classes.sectionTitle}>Word Spacing</Typography>
                <div className={classes.sliderLabel}>
                  <Typography variant="body2" style={{ color: '#777' }}>
                    {wordSpacing} px
                  </Typography>
                </div>
                <Slider
                  className={classes.slider}
                  value={wordSpacing}
                  min={-20}
                  max={150}
                  step={1}
                  onMouseDown={saveStateToHistory}
                  onChange={(_, val) => setWordSpacing(val)}
                />

                <Divider style={{ margin: '20px 0' }} />

                {/* Selected Letter Properties */}
                {selectedTokenIndices.length > 0 &&
                  (() => {
                    const baselineId = selectedTokenIndices[0];
                    return (
                      <>
                        <Typography className={classes.sectionTitle} style={{ color: '#533483' }}>
                          Selected Letters ({selectedTokenIndices.length}) Properties
                        </Typography>

                        {/* Size */}
                        <div className={classes.sliderLabel}>
                          <Typography variant="body2" style={{ color: '#777' }}>
                            Size: {tokenSizes[baselineId] || fontSize} px
                          </Typography>
                        </div>
                        <Slider
                          className={classes.slider}
                          value={tokenSizes[baselineId] || fontSize}
                          min={12}
                          max={120}
                          step={2}
                          onMouseDown={saveStateToHistory}
                          onChange={(_, val) => {
                            setTokenSizes((prev) => {
                              const next = { ...prev };
                              selectedTokenIndices.forEach((id) => (next[id] = val));
                              return next;
                            });
                          }}
                        />

                        {/* Width */}
                        <div className={classes.sliderLabel}>
                          <Typography variant="body2" style={{ color: '#777' }}>
                            Width: {tokenWidths[baselineId] ?? 100} %
                          </Typography>
                        </div>
                        <Slider
                          className={classes.slider}
                          value={tokenWidths[baselineId] ?? 100}
                          min={20}
                          max={300}
                          step={5}
                          onMouseDown={saveStateToHistory}
                          onChange={(_, val) => {
                            setTokenWidths((prev) => {
                              const next = { ...prev };
                              selectedTokenIndices.forEach((id) => (next[id] = val));
                              return next;
                            });
                          }}
                        />

                        {/* Height */}
                        <div className={classes.sliderLabel}>
                          <Typography variant="body2" style={{ color: '#777' }}>
                            Height: {tokenHeights[baselineId] ?? 100} %
                          </Typography>
                        </div>
                        <Slider
                          className={classes.slider}
                          value={tokenHeights[baselineId] ?? 100}
                          min={20}
                          max={300}
                          step={5}
                          onMouseDown={saveStateToHistory}
                          onChange={(_, val) => {
                            setTokenHeights((prev) => {
                              const next = { ...prev };
                              selectedTokenIndices.forEach((id) => (next[id] = val));
                              return next;
                            });
                          }}
                        />

                        {/* Spacing */}
                        <div className={classes.sliderLabel}>
                          <Typography variant="body2" style={{ color: '#777' }}>
                            Space After Letter: {tokenSpacings[baselineId] ?? 0} px
                          </Typography>
                        </div>
                        <Slider
                          className={classes.slider}
                          value={tokenSpacings[baselineId] ?? 0}
                          min={-50}
                          max={150}
                          step={1}
                          onMouseDown={saveStateToHistory}
                          onChange={(_, val) => {
                            setTokenSpacings((prev) => {
                              const next = { ...prev };
                              selectedTokenIndices.forEach((id) => (next[id] = val));
                              return next;
                            });
                          }}
                        />

                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              saveStateToHistory();
                              setTokenSizes((prev) => {
                                const n = { ...prev };
                                selectedTokenIndices.forEach((id) => delete n[id]);
                                return n;
                              });
                              setTokenWidths((prev) => {
                                const n = { ...prev };
                                selectedTokenIndices.forEach((id) => delete n[id]);
                                return n;
                              });
                              setTokenHeights((prev) => {
                                const n = { ...prev };
                                selectedTokenIndices.forEach((id) => delete n[id]);
                                return n;
                              });
                              setTokenSpacings((prev) => {
                                const n = { ...prev };
                                selectedTokenIndices.forEach((id) => delete n[id]);
                                return n;
                              });
                            }}
                          >
                            Reset Properties
                          </Button>
                          <Button size="small" variant="outlined" onClick={() => setSelectedTokenIndices([])}>
                            Deselect
                          </Button>
                        </div>
                        <Divider style={{ margin: '20px 0' }} />
                      </>
                    );
                  })()}

                {/* Size presets */}
                <Typography className={classes.sectionTitle}>Size Presets</Typography>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {[24, 36, 48, 72, 96, 120, 160, 200].map((s) => (
                    <Chip
                      key={s}
                      label={`${s}px`}
                      size="small"
                      color={fontSize === s ? 'primary' : 'default'}
                      onClick={() => {
                        saveStateToHistory();
                        setFontSize(s);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </div>
                <Typography variant="caption" style={{ color: '#aaa', marginTop: 8, display: 'block' }}>
                  Raster font — very large sizes may appear pixelated.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
    </Dialog>
  );
}
